<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useMediaQuery } from "@vueuse/core";

import { Check, Clipboard, Plus, UserRound, X } from "@lucide/vue";

import {
  Button,
  Input,
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui";

import { useServicesCatalogStore } from "../model/store";
import { useServiceAssign } from "../model/useServiceAssign";
import AssignedMasterList from "./AssignedMasterList.vue";
import InlineMasterCreate from "./InlineMasterCreate.vue";
import MasterPicker from "./MasterPicker.vue";
import MoveCategoryPicker from "./MoveCategoryPicker.vue";
import MasterServiceDetachDialog from "./MasterServiceDetachDialog.vue";
import ServiceDeleteDialog from "./ServiceDeleteDialog.vue";
import ServiceManageMenu from "./ServiceManageMenu.vue";
import ServiceMasterConfigStep from "./ServiceMasterConfigStep.vue";

/**
 * Detail sheet послуги (spec §7.2, 168-T4 + T5 + 169-T4..T7).
 * Стейт-машина: detail | rename | move (local)
 *               + picker | config (assign composable)
 */
const props = defineProps<{
  serviceId: string | null;
  open: boolean;
}>();

const emit = defineEmits<{
  "update:open": [value: boolean];
}>();

const { t } = useI18n();
const store = useServicesCatalogStore();
const { roster } = storeToRefs(store);
const isDesktop = useMediaQuery("(min-width: 768px)");

// ── Local step (rename / move / detail) ───────────────────────────────────────
type DetailStep = "detail" | "rename" | "move";
const step = ref<DetailStep>("detail");

// ── Assign composable ─────────────────────────────────────────────────────────
const assign = useServiceAssign(roster, () => store.load());

// ── Rename state ──────────────────────────────────────────────────────────────
const editName = ref("");
const renameStatus = ref<"idle" | "saving">("idle");

// ── Delete dialog state ───────────────────────────────────────────────────────
const deleteOpen = ref(false);

// ── Detach dialog state ───────────────────────────────────────────────────────
const detachDialogOpen = ref(false);

// ── Derived from store ────────────────────────────────────────────────────────
const service = computed(() => {
  if (!props.serviceId) return null;
  const row = store.rows.find(
    (r) => r.kind === "saved" && r.service.id === props.serviceId,
  );
  return row?.kind === "saved" ? row.service : null;
});

// true when the detail view renders (both empty-state and has-masters sub-branches)
// used to hide the built-in SheetContent close and render our own inline close
const isDetailStep = computed(
  () => step.value === "detail" && assign.step.value === "detail",
);

const categoryName = computed(() => {
  if (!service.value?.categoryId) return null;
  return store.categories.find((c) => c.id === service.value!.categoryId)?.name ?? null;
});

const subtitle = computed(() => {
  if (!service.value) return "";
  // Use live list length when loaded; fall back to catalog snapshot otherwise
  const count = assign.assignedStatus.value === "ready"
    ? assign.assigned.value.length
    : service.value.assignedMasterCount;
  const masterLabel = t("services.detail.masterCount", { count });
  return categoryName.value ? `${categoryName.value} · ${masterLabel}` : masterLabel;
});

// ── Reset when sheet closes or service changes ────────────────────────────────
watch(
  () => [props.open, props.serviceId] as const,
  ([open, serviceId], prev) => {
    const prevOpen = prev?.[0];
    if (!open) {
      step.value = "detail";
      editName.value = "";
      renameStatus.value = "idle";
      deleteOpen.value = false;
      detachDialogOpen.value = false;
      assign.backToDetail();
    } else if (open && serviceId) {
      // Load assigned masters when opening (W1: fires on mount too via immediate: true)
      if (!prevOpen) {
        void assign.loadAssigned(serviceId);
      }
    }
  },
  { immediate: true },
);

// ── Watch staleClose from composable ─────────────────────────────────────────
watch(assign.staleClose, (stale) => {
  if (stale) {
    close();
  }
});

// ── Actions ───────────────────────────────────────────────────────────────────
function openRename(): void {
  editName.value = service.value?.name ?? "";
  step.value = "rename";
}

function cancelRename(): void {
  if (props.serviceId) store.cancelRename(props.serviceId);
  step.value = "detail";
  editName.value = "";
}

async function confirmRename(): Promise<void> {
  if (!props.serviceId || renameStatus.value === "saving") return;
  renameStatus.value = "saving";
  await store.commitRename(
    props.serviceId,
    editName.value,
    service.value?.categoryId ?? null,
  );
  renameStatus.value = "idle";
  step.value = "detail";
}

async function confirmMove(newCategoryId: string | null): Promise<void> {
  if (!props.serviceId || !service.value) return;
  await store.commitRename(
    props.serviceId,
    service.value.name,
    newCategoryId,
  );
  step.value = "detail";
}

function onDeletedOrGone(): void {
  emit("update:open", false);
}

function close(): void {
  emit("update:open", false);
}

// ── Config step master name helper ────────────────────────────────────────────
const configMasterName = computed(() => {
  const masterId = assign.pickerSelectedMasterId.value;
  if (!masterId) return "";
  const fromAssigned = assign.assigned.value.find((a) => a.masterId === masterId)?.name;
  if (fromAssigned) return fromAssigned;
  return assign.candidates.value.find((c) => c.masterId === masterId)?.name ?? "";
});

// true only when the selected master is already in the assigned list (edit flow)
const isEditingAssignedMaster = computed(() =>
  assign.assigned.value.some((a) => a.masterId === assign.pickerSelectedMasterId.value),
);
</script>

<template>
  <Sheet
    :open="open"
    @update:open="close"
  >
    <SheetContent
      :side="isDesktop ? 'right' : 'bottom'"
      :show-close="!isDetailStep"
      class="service-detail-sheet"
      data-testid="service-detail-sheet"
    >
      <!-- ── RENAME STEP ─────────────────────────────────────────────────────── -->
      <template v-if="step === 'rename'">
        <SheetHeader>
          <SheetTitle class="service-detail-sheet__rename-header">
            <Input
              v-model="editName"
              class="service-detail-sheet__rename-input"
              data-testid="rename-input"
              :disabled="renameStatus === 'saving'"
              @keydown.enter.prevent="confirmRename"
              @keydown.escape.prevent="cancelRename"
            />
            <Button
              size="sm"
              variant="ghost"
              :disabled="!editName.trim() || renameStatus === 'saving'"
              :aria-label="t('services.detail.renameConfirm')"
              data-testid="rename-confirm"
              @click="confirmRename"
            >
              ✓
            </Button>
            <Button
              size="sm"
              variant="ghost"
              :aria-label="t('services.detail.renameCancel')"
              data-testid="rename-cancel"
              @click="cancelRename"
            >
              ✕
            </Button>
          </SheetTitle>
        </SheetHeader>
      </template>

      <!-- ── MOVE STEP ──────────────────────────────────────────────────────── -->
      <template v-else-if="step === 'move'">
        <SheetHeader>
          <SheetTitle>{{ t("services.actions.moveToCategory") }}</SheetTitle>
        </SheetHeader>
        <MoveCategoryPicker
          v-if="service"
          :current-category-id="service.categoryId ?? null"
          :categories="store.categories"
          @select="confirmMove"
          @cancel="step = 'detail'"
        />
      </template>

      <!-- ── PICKER STEP (assign composable) ───────────────────────────────── -->
      <template v-else-if="assign.step.value === 'picker'">
        <SheetHeader>
          <SheetTitle>{{ t("services.assign.picker.title") }}</SheetTitle>
        </SheetHeader>

        <!-- Inline create form (T7) -->
        <InlineMasterCreate
          v-if="assign.inlineCreate.open"
          :name="assign.inlineCreate.name"
          :phone="assign.inlineCreate.phone"
          :status="assign.inlineCreate.status"
          :error="assign.inlineCreate.error"
          @update:name="assign.inlineCreate.name = $event"
          @update:phone="assign.inlineCreate.phone = $event"
          @submit="assign.createMasterInline()"
        />

        <MasterPicker
          v-else
          :candidates="assign.candidates.value"
          :selected-master-id="assign.pickerSelectedMasterId.value"
          :show-search="assign.showSearch.value"
          :picker-search="assign.pickerSearch.value"
          @select="assign.pickerSelectedMasterId.value = $event"
          @next="assign.goToConfig(assign.pickerSelectedMasterId.value!)"
          @create-new="assign.openInlineCreate()"
          @update:picker-search="assign.pickerSearch.value = $event"
        />
      </template>

      <!-- ── CONFIG STEP (assign composable) ───────────────────────────────── -->
      <template v-else-if="assign.step.value === 'config'">
        <SheetHeader>
          <SheetTitle>{{ t("services.assign.config.save") }}</SheetTitle>
        </SheetHeader>
        <ServiceMasterConfigStep
          :master-name="configMasterName"
          :service-name="service?.name ?? ''"
          :master-id="assign.pickerSelectedMasterId.value ?? ''"
          :config="assign.config"
          :can-detach="isEditingAssignedMaster"
          @save="assign.saveConfig()"
          @cancel="assign.cancelConfig()"
          @detach="detachDialogOpen = true"
          @update:duration="assign.config.duration = $event"
          @update:price="assign.config.price = $event"
          @update:buffer="assign.config.buffer = $event"
        />
        <MasterServiceDetachDialog
          :open="detachDialogOpen"
          :master-name="configMasterName"
          :service-name="service?.name ?? ''"
          :detach="() => assign.detach(assign.pickerSelectedMasterId.value ?? '')"
          @update:open="(v) => { if (!v) detachDialogOpen = false }"
          @detached="detachDialogOpen = false"
        />
      </template>

      <!-- ── DETAIL STEP (default) ──────────────────────────────────────────── -->
      <template v-else>
        <!-- ── EMPTY STATE (0 masters) ─────────────────────────────────────── -->
        <template v-if="service && service.assignedMasterCount === 0">
          <SheetHeader class="service-detail-sheet__header">
            <div class="service-detail-sheet__header-row">
              <SheetTitle class="service-detail-sheet__title">
                {{ t("services.detail.sheetTitle") }}
              </SheetTitle>
              <div class="service-detail-sheet__header-actions">
                <ServiceManageMenu
                  v-if="service"
                  :aria-label="t('services.detail.manageMenuLabel')"
                  @rename="openRename"
                  @move="step = 'move'"
                  @delete="deleteOpen = true"
                />
                <SheetClose as-child>
                  <Button
                    variant="ghost"
                    size="icon"
                    :aria-label="t('services.detail.closeLabel')"
                  >
                    <X aria-hidden="true" />
                  </Button>
                </SheetClose>
              </div>
            </div>
            <div class="service-detail-sheet__ctx-chip">
              <Clipboard
                class="service-detail-sheet__ctx-chip-icon"
                aria-hidden="true"
              />
              <div>
                <div class="service-detail-sheet__ctx-label">
                  {{ t("services.detail.ctxLabel") }}
                </div>
                <div
                  class="service-detail-sheet__ctx-val"
                  data-testid="sheet-service-name"
                >
                  {{ service.name }}
                </div>
              </div>
            </div>
          </SheetHeader>

          <div
            class="service-detail-sheet__empty-state"
            data-testid="f1-empty"
          >
            <div
              class="service-detail-sheet__empty-icon"
              aria-hidden="true"
            >
              <UserRound class="service-detail-sheet__empty-icon-svg" />
            </div>
            <p class="service-detail-sheet__empty-title">
              {{ t("services.detail.empty.title") }}
            </p>
            <p class="service-detail-sheet__empty-body">
              {{ t("services.detail.empty.body") }}
            </p>
          </div>

          <SheetFooter class="service-detail-sheet__footer">
            <Button
              class="service-detail-sheet__cta"
              data-testid="add-master-cta"
              @click="assign.goToPicker()"
            >
              <Plus aria-hidden="true" />
              {{ t("services.detail.addMaster") }}
            </Button>
          </SheetFooter>
        </template>

        <!-- ── HAS-MASTERS / NULL STATE ─────────────────────────────────────── -->
        <template v-else>
          <SheetHeader class="service-detail-sheet__header">
            <div class="service-detail-sheet__header-row">
              <SheetTitle
                class="service-detail-sheet__title"
                data-testid="sheet-service-name"
              >
                {{ service?.name ?? "" }}
              </SheetTitle>
              <div class="service-detail-sheet__header-actions">
                <ServiceManageMenu
                  v-if="service"
                  :aria-label="t('services.detail.manageMenuLabel')"
                  @rename="openRename"
                  @move="step = 'move'"
                  @delete="deleteOpen = true"
                />
                <SheetClose as-child>
                  <Button
                    variant="ghost"
                    size="icon"
                    :aria-label="t('services.detail.closeLabel')"
                  >
                    <X aria-hidden="true" />
                  </Button>
                </SheetClose>
              </div>
            </div>
            <p
              v-if="subtitle"
              class="service-detail-sheet__subtitle text-sm text-muted-foreground"
              data-testid="sheet-subtitle"
            >
              {{ subtitle }}
            </p>
          </SheetHeader>

          <div class="service-detail-sheet__body">
            <!-- Success banner (GAP 9) -->
            <div
              v-if="assign.successBanner.value"
              class="service-detail-sheet__banner"
              data-testid="assign-success-banner"
              role="status"
            >
              <Check
                class="service-detail-sheet__banner-icon"
                aria-hidden="true"
              />
              {{ t("services.detail.bannerAdded", {
                name: assign.successBannerMasterName.value,
                service: service?.name ?? "",
              }) }}
            </div>

            <!-- Masters section header -->
            <h3 class="service-detail-sheet__section-title">
              {{ t("services.detail.mastersSection") }}
            </h3>

            <template v-if="service && service.assignedMasterCount > 0">
              <AssignedMasterList
                :assigned="assign.assigned.value"
                :assigned-status="assign.assignedStatus.value"
                @add-master="assign.goToPicker()"
                @edit-master="assign.goToConfig"
                @retry="serviceId && assign.loadAssigned(serviceId)"
              />
            </template>
          </div>
        </template>

        <!-- Delete dialog: shared between empty + has-masters branches -->
        <ServiceDeleteDialog
          v-if="service"
          :open="deleteOpen"
          :service="service"
          @update:open="(v) => { deleteOpen = v; }"
          @deleted="onDeletedOrGone"
        />
      </template>
    </SheetContent>
  </Sheet>
</template>

<style scoped>
.service-detail-sheet__header {
  padding-block-end: 0.5rem;
}

.service-detail-sheet__header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
}

.service-detail-sheet__header-actions {
  display: flex;
  align-items: center;
  gap: 0.125rem;
  flex-shrink: 0;
  margin-block-start: -0.25rem;
}

.service-detail-sheet__title {
  flex: 1;
  overflow-wrap: break-word;
  word-break: break-word;
}

.service-detail-sheet__rename-header {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  /* reserve space for SheetContent's built-in close button (top-right) */
  padding-inline-end: 2.5rem;
}

.service-detail-sheet__rename-input {
  flex: 1;
}

.service-detail-sheet__body {
  padding-block-start: 1rem;
}

/* GAP 8: секція «Майстри» — label-стиль */
.service-detail-sheet__section-title {
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--muted-foreground);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-block-end: 0.25rem;
}

/* ── Context chip (empty state) ───────────────────────────────────────── */
.service-detail-sheet__ctx-chip {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--accent);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: 0.5625rem 0.75rem;
  margin-block-start: 0.125rem;
}

.service-detail-sheet__ctx-chip-icon {
  width: 0.9375rem;
  height: 0.9375rem;
  color: var(--primary);
  flex-shrink: 0;
}

.service-detail-sheet__ctx-label {
  font-size: 0.6875rem;
  color: var(--muted-foreground);
}

.service-detail-sheet__ctx-val {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--foreground);
}

/* ── Empty state block ────────────────────────────────────────────────── */
.service-detail-sheet__empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1rem 0.375rem;
  text-align: center;
}

.service-detail-sheet__empty-icon {
  width: 3rem;
  height: 3rem;
  border-radius: var(--radius);
  background: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
}

.service-detail-sheet__empty-icon-svg {
  width: 1.5rem;
  height: 1.5rem;
  color: var(--primary);
}

.service-detail-sheet__empty-title {
  font-size: 0.875rem;
  font-weight: 600;
}

.service-detail-sheet__empty-body {
  font-size: 0.75rem;
  color: var(--muted-foreground);
  max-width: 15.625rem;
  line-height: 1.5;
}

/* ── CTA footer (empty state) ─────────────────────────────────────────── */
.service-detail-sheet__footer {
  flex-shrink: 0;
  padding-block-start: 0.875rem;
}

.service-detail-sheet__cta {
  width: 100%;
  min-height: 2.75rem;
}

.service-detail-sheet__subtitle {
  margin-block-start: 0.25rem;
}

/* GAP 9: success banner — green */
.service-detail-sheet__banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5625rem 0.75rem;
  margin-block-end: 0.625rem;
  background-color: var(--success-subtle);
  border: 1px solid color-mix(in oklch, var(--success) 30%, var(--success-subtle));
  border-radius: var(--radius-md);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--success-foreground);
}

.service-detail-sheet__banner-icon {
  width: 0.9375rem;
  height: 0.9375rem;
  flex-shrink: 0;
}

</style>
